import React, { useRef, useState } from 'react';
import { InputAdornment, Popper } from '@material-ui/core';
import DayPicker, { BeforeModifier, Modifiers, RangeModifier, DateUtils } from 'react-day-picker';
import 'react-day-picker/lib/style.css';
import DateRangeIcon from '@material-ui/icons/DateRange';
import { ReferenceObject } from 'popper.js';
import { Controller, useFormContext } from 'react-hook-form';
import dayjs from 'dayjs';

import { useOnClickOutside } from '../../../hooks/useOnClickOutside/useOnClickOutside';

import { ScanFieldProps } from './DateRangeField.types';
import { useStyles } from './DateRangeField.styles';

import { GenericField } from '..';

export const DateRangeField = ({ ...props }: ScanFieldProps) => {
  const classes = useStyles();
  const { control, setValue } = useFormContext();
  const [{ from, to, enteredTo }, setDate] = useState<{ from?: Date; to?: Date; enteredTo?: Date }>({
    from: undefined,
    to: undefined,
    enteredTo: undefined,
  });
  const [anchorEl, setAnchorEl] = useState<ReferenceObject | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => setAnchorEl(null));

  const handleClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
  };

  const isSelectingFirstDay = (day: Date) => {
    const isBeforeFirstDay = from && DateUtils.isDayBefore(day, from);
    const isRangeSelected = from && to;
    return !from || isBeforeFirstDay || isRangeSelected;
  };

  const handleDayClick = (day: Date) => {
    if (from && to && day >= from && day <= to) {
      handleResetClick();
      setValue(props.name, { from: undefined, to: undefined });
      return;
    }
    if (isSelectingFirstDay(day)) {
      setDate({
        from: day,
        to: undefined,
        enteredTo: undefined,
      });
    } else {
      setDate(prev => ({
        ...prev,
        to: day,
        enteredTo: day,
      }));

      from && setValue(props.name, { from, to: day });
    }
  };

  const handleDayMouseEnter = (day: Date) => {
    if (!isSelectingFirstDay(day)) {
      setDate(prev => ({
        ...prev,
        enteredTo: day,
      }));
    }
  };

  const handleResetClick = () => {
    setDate({
      from: undefined,
      to: undefined,
      enteredTo: undefined,
    });
  };

  const parseDate = (date: Date) => dayjs(date).format('YYYY-MM-DD');
  return (
    <>
      <Controller
        name={props.name}
        render={({ value }) => (
          <GenericField
            {...props}
            onClick={handleClick}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <DateRangeIcon color="inherit" className={classes.calendarIcon} />
                </InputAdornment>
              ),
            }}
            value={value.from && value.to ? `${parseDate(value.from)} - ${parseDate(value.to)}` : ''}
          />
        )}
        control={control}
        defaultValue={{
          from,
          to,
        }}
      />

      <Popper open={Boolean(anchorEl)} anchorEl={anchorEl}>
        <div ref={ref}>
          <DayPicker
            numberOfMonths={2}
            selectedDays={[from, { from, to: enteredTo }] as RangeModifier[]}
            disabledDays={{ before: from } as BeforeModifier}
            modifiers={{ start: from, end: enteredTo } as Partial<Modifiers>}
            onDayClick={handleDayClick}
            onDayMouseEnter={handleDayMouseEnter}
            className={classes.rangeContainer}
          />
        </div>
      </Popper>
    </>
  );
};
